import { parse, unparse } from 'papaparse';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { addMonths, isValid, parse as parseDate } from 'date-fns';
import { toast } from 'sonner';

import type { SourcePlatform } from '~entities/import-job/api/types';
import { useImportHeaderAliases } from '~entities/import-job/api/import-job-api';
import { buildClassColumnMappingTable } from '~features/import-wizard/build-class-column-mapping';
import {
  IMPORT_CLASS_TEMPLATE_URL,
} from '~features/import-wizard/class-mapping-table-data';
import {
  getClassAliasesForPlatform,
  getProviderExportHeaders,
  isJackrabbitFamilyExport,
} from '~features/import-wizard/header-aliases-fallback';
import { ImportWizardCsvSelector } from '~features/import-wizard/import-wizard-csv-selector';
import {
  normalizeCsvHeaders,
  normalizeCsvRows,
} from '~features/import-wizard/parse-csv-headers';
import {
  collectUnmappedClassHeadersToAppend,
  downloadCsvWithAppendedColumns,
  downloadMappedCsv,
} from '~features/import-wizard/download-csv';
import { PlatformMismatchWarning } from '~features/import-wizard/platform-mismatch-warning';
import { summarizeValidation } from '~features/import-wizard/validation-summary';
import { ValidationSummaryPanel } from '~features/import-wizard/validation-summary-panel';
import {
  applyPlatformClassRowTransforms,
  enrichDancebizClassRows,
} from '~features/import-wizard/platform-row-transforms';
import { findUnmappableRequiredClassFields } from '~features/import-wizard/unmappable-required-class-fields';
import {
  headersFromRowKeys,
  RestoredCsvBanner,
} from '~features/import-wizard/restored-csv-banner';
import { MappingGuidance } from '~features/import-wizard/mapping-guidance';
import { ColumnMapping } from '~pages/classes/bulk-upload/column-mapping';
import { ValidateCSV } from '~pages/classes/bulk-upload/validate-csv';
import {
  type ClassColumnMappingTableData,
  REQUIRED_CLASS_HEADERS,
} from '~pages/classes/bulk-upload/types';
import {
  type ClassData,
  validateCell as validateClassCell,
} from '~pages/classes/bulk-upload/validation';

import { Button } from '~shared/shadcn/ui/button';
import { AlertTriangle } from 'lucide-react';

function parseEndDate(value: string | undefined): Date | null {
  if (!value?.trim()) return null;
  for (const fmt of ['MM/dd/yyyy', 'MM-dd-yyyy'] as const) {
    const d = parseDate(value.trim(), fmt, new Date());
    if (isValid(d)) return d;
  }
  return null;
}

export interface ClassWizardResult {
  file: File;
  rows: Array<ClassData>;
  titles: Array<{ rowIndex: number; title: string }>;
  endDateWarnings: Array<{ row: number; message: string; suggested?: string }>;
}

interface Step1ClassesProps {
  sourcePlatform: SourcePlatform;
  restoredResult?: ClassWizardResult | null;
  onRestoredClear?: () => void;
  onComplete: (result: ClassWizardResult | null) => void;
  onBack: () => void;
  onSkip: () => void | Promise<void>;
}

function buildInitialClassState(restoredResult?: ClassWizardResult | null) {
  if (!restoredResult?.file || restoredResult.rows.length === 0) {
    return {
      subStep: 'select' as const,
      file: null as File | null,
      csvData: [] as Array<ClassData>,
      headers: [] as Array<string>,
      fromSession: false,
    };
  }
  return {
    subStep: 'validate' as const,
    file: restoredResult.file,
    csvData: restoredResult.rows,
    headers: headersFromRowKeys(restoredResult.rows),
    fromSession: true,
  };
}

export function Step1Classes({
  sourcePlatform,
  restoredResult,
  onRestoredClear,
  onComplete,
  onBack,
  onSkip,
}: Step1ClassesProps) {
  const initial = buildInitialClassState(restoredResult);
  const [subStep, setSubStepState] = useState<
    'select' | 'mapping' | 'validate'
  >(initial.subStep);
  const [file, setFile] = useState<File | null>(initial.file);
  const [csvData, setCSVData] = useState<Array<ClassData>>(initial.csvData);
  const [headers, setHeaders] = useState<Array<string>>(initial.headers);
  const [fromSession, setFromSession] = useState(initial.fromSession);
  const [columnMappingTableData, setColumnMappingTableData] = useState<
    Array<ClassColumnMappingTableData>
  >([]);
  const { data: headerAliases } = useImportHeaderAliases();
  const classAliases = useMemo(
    () => getClassAliasesForPlatform(sourcePlatform, headerAliases),
    [headerAliases, sourcePlatform],
  );
  const expectedHeaders = useMemo(
    () => getProviderExportHeaders(sourcePlatform, headerAliases).classes,
    [headerAliases, sourcePlatform],
  );

  const validationSummary = useMemo(
    () =>
      summarizeValidation(
        csvData as Array<Record<string, unknown>>,
        (key, value, row) =>
          validateClassCell(
            key as keyof ClassData,
            value,
            row as Partial<ClassData>,
          ),
      ),
    [csvData],
  );

  const unmappableRequiredFields = useMemo(
    () =>
      headers.length === 0
        ? []
        : findUnmappableRequiredClassFields(
            headers,
            classAliases,
            sourcePlatform,
          ),
    [headers, classAliases, sourcePlatform],
  );

  const applyColumnPreMap = useCallback(
    (
      allHeaders: Array<string>,
      parsedCSVData: Array<ClassData>,
    ) => {
      const csvRow = parsedCSVData[0];
      setColumnMappingTableData(
        buildClassColumnMappingTable(
          allHeaders,
          csvRow,
          classAliases,
          sourcePlatform,
        ),
      );
    },
    [classAliases, sourcePlatform],
  );

  useEffect(() => {
    if (subStep !== 'mapping' || headers.length === 0 || csvData.length === 0) {
      return;
    }
    applyColumnPreMap(headers, csvData);
  }, [subStep, headers, csvData, applyColumnPreMap, sourcePlatform, classAliases]);

  const onFileChange = useCallback(
    (f: File | null) => {
      if (!f) return;
      setFromSession(false);
      onRestoredClear?.();
      setFile(f);
      parse<ClassData>(f, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          const rawHeaders = result.meta.fields ?? [];
          const parsedHeaders = normalizeCsvHeaders(rawHeaders);
          let parsedCSVData = normalizeCsvRows(result.data, rawHeaders) as Array<ClassData>;
          if (
            sourcePlatform === 'jackrabbit' ||
            sourcePlatform === 'studio_director'
          ) {
            parsedCSVData = enrichDancebizClassRows(parsedCSVData);
          }
          if (
            sourcePlatform === 'jackrabbit' &&
            isJackrabbitFamilyExport(parsedHeaders)
          ) {
            toast.message(
              'This file looks like a family export. Use step "Your families" instead of the classes step.',
              { duration: 8000 },
            );
          }
          const hasComputedEndTime =
            (sourcePlatform === 'jackrabbit' ||
              sourcePlatform === 'studio_director') &&
            parsedCSVData.some((row) => String(row['End Time'] ?? '').trim());
          const allHeaders =
            hasComputedEndTime && !parsedHeaders.includes('End Time')
              ? [...parsedHeaders, 'End Time']
              : parsedHeaders;
          setCSVData(parsedCSVData);
          setHeaders(allHeaders);
          applyColumnPreMap(allHeaders, parsedCSVData);
        },
      });
    },
    [applyColumnPreMap, onRestoredClear, sourcePlatform],
  );

  const handleReplaceRestored = () => {
    setFromSession(false);
    onRestoredClear?.();
    setFile(null);
    setCSVData([]);
    setHeaders([]);
    setColumnMappingTableData([]);
    setSubStepState('select');
  };

  const setSubStep = (next: 'select' | 'mapping' | 'validate') => {
    setSubStepState(next);
  };

  const handleFinish = () => {
    if (!file) {
      onComplete(null);
      return;
    }
    const softLimit = addMonths(new Date(), 18);
    const warnings: ClassWizardResult['endDateWarnings'] = [];
    csvData.forEach((row, idx) => {
      const end = parseEndDate(row['End Date']);
      if (end && end > softLimit) {
        warnings.push({
          row: idx + 2,
          message: `Row ${idx + 2}: "${row.Title}" ends far in the future (${row['End Date']}).`,
          suggested: `${softLimit.getMonth() + 1}/${softLimit.getDate()}/${softLimit.getFullYear()}`,
        });
      }
    });
    const transformedRows = applyPlatformClassRowTransforms(csvData, sourcePlatform);
    const titles = transformedRows
      .map((row, rowIndex) => ({
        rowIndex,
        title: String(row.Title ?? '').trim(),
      }))
      .filter((t) => t.title);
    const csvString = unparse(transformedRows);
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const outFile = new File([blob], file.name || 'classes.csv', {
      type: 'text/csv',
    });
    onComplete({ file: outFile, rows: transformedRows, titles, endDateWarnings: warnings });
  };

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <h2 className='text-xl font-semibold'>Your classes</h2>
        <Button variant='ghost' size='sm' onClick={onSkip}>
          Skip classes
        </Button>
      </div>
      {expectedHeaders.length > 0 && (
        <p className='text-sm text-muted-foreground'>
          Expected columns for your provider:{' '}
          <span className='font-medium text-foreground'>
            {expectedHeaders.join(', ')}
          </span>
        </p>
      )}
      {fromSession && file && (
        <RestoredCsvBanner
          filename={file.name}
          rowCount={csvData.length}
          datasetLabel='class row'
          onReplace={handleReplaceRestored}
        />
      )}
      {subStep === 'select' && (
        <>
        <ImportWizardCsvSelector
          file={file}
          setFile={onFileChange}
          title='Upload your class list'
          description='Columns that match the Enrollio template or your previous system are selected automatically on the next step.'
          sampleCsvUrl={IMPORT_CLASS_TEMPLATE_URL}
          sampleCsvDownloadName='import-classes-template.csv'
          onNext={() => {
            if (headers.length > 0 && csvData.length > 0) {
              applyColumnPreMap(headers, csvData);
            }
            setSubStep('mapping');
          }}
        />
          <div className='flex flex-wrap justify-start gap-2'>
            <Button variant='outline' onClick={onBack}>
              Back
            </Button>
            {fromSession && csvData.length > 0 && (
              <Button variant='secondary' onClick={() => setSubStep('validate')}>
                Review restored data
              </Button>
            )}
          </div>
        </>
      )}
      {subStep === 'mapping' && (
        <>
          <MappingGuidance
            datasetLabel='class'
            requiredFields={REQUIRED_CLASS_HEADERS}
          />
          {unmappableRequiredFields.length > 0 && (
            <div className='rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900'>
              <div className='flex items-start gap-2 font-medium'>
                <AlertTriangle className='mt-0.5 size-4 shrink-0' />
                <span>
                  This CSV is missing columns needed for required fields:{' '}
                  {unmappableRequiredFields.join(', ')}.
                </span>
              </div>
              <p className='mt-2 text-red-800'>
                Download keeps your original CSV columns and data, then appends
                empty columns for every Enrollio field that has no mapping yet.
                Fill those cells, then re-upload. You cannot finish mapping
                classes until every required field can be mapped. You can still
                use <span className='font-medium'>Skip classes</span> — class
                photos stay unavailable until class mapping is completed.
              </p>
              <div className='mt-3'>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  className='border-red-300 bg-white text-red-900 hover:bg-red-100'
                  onClick={() => {
                    if (!file) {
                      toast.error('Upload a CSV first, then download again.');
                      return;
                    }
                    const baseName =
                      file.name.replace(/\.csv$/i, '').trim() || 'classes';
                    const filename = `${baseName}-with-required-columns.csv`;
                    const columnsToAppend =
                      collectUnmappedClassHeadersToAppend(
                        columnMappingTableData,
                      );
                    parse(file, {
                      header: true,
                      skipEmptyLines: true,
                      complete: (result) => {
                        const rawHeaders = result.meta.fields ?? [];
                        const originalHeaders = normalizeCsvHeaders(rawHeaders);
                        const originalRows = normalizeCsvRows(
                          result.data as Array<Record<string, unknown>>,
                          rawHeaders,
                        );
                        downloadCsvWithAppendedColumns(
                          originalRows,
                          originalHeaders,
                          columnsToAppend,
                          filename,
                        );
                        toast.message(
                          'Downloaded original CSV with unmapped columns added. Fill them in, then re-upload.',
                        );
                      },
                      error: () => {
                        toast.error(
                          'Could not read the original CSV. Try re-uploading the file.',
                        );
                      },
                    });
                  }}
                >
                  Download CSV with missing columns
                </Button>
              </div>
            </div>
          )}
          <PlatformMismatchWarning
            sourcePlatform={sourcePlatform}
            uploadedHeaders={headers}
            expectedHeaders={expectedHeaders}
            datasetLabel='class'
          />
          <ColumnMapping
            setStep={setSubStep}
            columnHeaders={headers}
            columnMappingTableData={columnMappingTableData}
            setColumnMappingTableData={setColumnMappingTableData}
            csvData={csvData}
            setCSVData={setCSVData}
          />
        </>
      )}
      {subStep === 'validate' && (
        <>
          <ValidationSummaryPanel
            summary={validationSummary}
            totalRows={csvData.length}
          />
          <ValidateCSV csvData={csvData} embedded />
          {csvData.some((row) => {
            const end = parseEndDate(row['End Date']);
            return end && end > addMonths(new Date(), 18);
          }) && (
            <div className='rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900'>
              A few classes end far in the future. That can cause billing issues
              — review End Date before continuing.
            </div>
          )}
          <div className='flex flex-wrap items-center justify-between gap-2'>
            <Button variant='outline' onClick={() => setSubStep('mapping')}>
              Back
            </Button>
            <div className='flex flex-wrap gap-2'>
              <Button
                variant='secondary'
                onClick={() =>
                  downloadMappedCsv(
                    csvData as Array<Record<string, unknown>>,
                    'mapped-classes.csv',
                  )
                }
              >
                Download mapped CSV
              </Button>
              <Button onClick={handleFinish}>Continue</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
